import { useMemo, useRef } from 'react';
import { Paragraph, StyledIcon, StyledMenuContainer, Wrapper } from './styles';
import KizenTypography from 'app/kizentypo';
import { useTranslation } from 'react-i18next';
import HandleIcon from '../Dashlet/HandleIcon';
import DashletMenuController from '../Dashlet/MenuController';
import { PillDateRangePicker } from '../Dashlet/styles';
import { useDateRangePickerShortcut } from 'components/Kizen/DateRangePicker/hooks';

const DashletError = ({
  onDelete,
  onRefresh,
  error,
  isDraggable,
  dashlet,
  onShowEditModal,
  showDateShortcut,
  todayDateInBusinessTimeZone,
  mobile,
  editDashlet,
  ranges,
  clientObjectId,
  ModalComponent,
  model,
  override,
  modalComponentProps,
  charts,
  canEdit,
  padding,
  center,
}) => {
  const { t } = useTranslation();
  const datePickerWrapperRef = useRef(null);
  const {
    datePickerDirection,
    containerRef,
    updateDashletDateFilter,
    dateFilterValue,
    unitsWidth,
  } = useDateRangePickerShortcut({
    ref: datePickerWrapperRef,
    currentDashlet: dashlet,
    editDashlet,
    onRefresh,
    showDateShortcut,
    ranges,
    t,
  });

  const errorValues = useMemo(() => {
    return Object.values(error?.response?.data?.errors ?? {});
  }, [error]);

  const useErrors =
    error?.response?.data?.errors &&
    typeof error.response.data.errors === 'object' &&
    errorValues.length &&
    errorValues.every((e) => typeof e === 'string');

  return (
    <Wrapper
      ref={containerRef}
      data-qa-error-status={error?.response?.status}
      padding={padding}
    >
      <StyledIcon icon="warning" />

      {canEdit && (
        <>
          {isDraggable && <HandleIcon />}
          <StyledMenuContainer>
            <DashletMenuController
              dashlet={dashlet}
              onShowEditModal={onShowEditModal}
              onDeleteDashlet={onDelete}
              clientObjectId={clientObjectId}
              ModalComponent={ModalComponent}
              model={model}
              modalComponentProps={modalComponentProps}
              entity={charts ? t('Chart') : t('Dashlet')}
            />
            {showDateShortcut && (
              <PillDateRangePicker
                paddingLeft={15}
                key={todayDateInBusinessTimeZone}
                direction={datePickerDirection}
                canEdit={canEdit && !mobile}
                value={dateFilterValue.range}
                selectedIndex={dateFilterValue.selectedIndex}
                onOk={(range, selectedIndex) =>
                  updateDashletDateFilter(range, selectedIndex, dashlet)
                }
                dashletUnits={unitsWidth}
                popover
                ranges={ranges}
                todayDate={todayDateInBusinessTimeZone}
              />
            )}
          </StyledMenuContainer>
        </>
      )}

      <Paragraph data-qa-id="dashlet-permission-error-text" center={center}>
        {override ? (
          override
        ) : (
          <>
            {useErrors ? (
              <>
                {Object.values(error.response.data.errors).map((error) => {
                  return error;
                })}{' '}
                {t('If the error persists, try')}
              </>
            ) : (
              t(
                'This {{entity}} is attempting to display missing or invalid data. Try',
                {
                  entity: charts ? t('chart') : t('dashlet'),
                }
              )
            )}{' '}
            <KizenTypography
              type="anchor"
              size="inherit"
              tabIndex="0" // Affects styles per bootstrap
              onClick={onRefresh}
            >
              {t('reloading the {{entity}}', {
                entity: charts ? t('chart') : t('dashlet'),
              })}
            </KizenTypography>
            {canEdit ? (
              <>
                {' '}
                {t('or')}{' '}
                <KizenTypography
                  type="anchor"
                  size="inherit"
                  tabIndex="0" // Affects styles per bootstrap
                  onClick={onDelete}
                >
                  {t('deleting the {{entity}}', {
                    entity: charts ? t('chart') : t('dashlet'),
                  })}
                </KizenTypography>
                .{' '}
              </>
            ) : (
              <>. </>
            )}
            {t(
              'If you continue to experience issues, please reach out to us at'
            )}{' '}
            <KizenTypography
              type="anchor"
              size="inherit"
              href="mailto:support@kizen.com"
            >
              Support@Kizen.com
            </KizenTypography>
            .
          </>
        )}
      </Paragraph>
    </Wrapper>
  );
};

export default DashletError;
